import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './ui/PageHeader';
import { AdminQueueState } from './ui/AdminQueueState';
import apiService from '../services/ApiService';
import type {
  FeatureFlagsCatalog,
  FeatureFlagsCatalogDomain,
} from '../services/api/featureFlagsCatalog';

function FlagTable({ flags }: { flags: Record<string, boolean> }) {
  const entries = Object.entries(flags);
  if (entries.length === 0) {
    return <p className="text-sm text-gray-500">No flags readable.</p>;
  }
  return (
    <table className="min-w-full text-sm text-left">
      <thead className="text-gray-500">
        <tr>
          <th className="py-1.5 pr-3 font-medium">Key</th>
          <th className="py-1.5 font-medium">Value</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([key, on]) => (
          <tr key={key} className="border-t border-white/5 text-gray-200">
            <td className="py-1.5 pr-3 font-mono text-xs">{key}</td>
            <td className="py-1.5">
              <span className={on ? 'text-emerald-400' : 'text-gray-500'}>{on ? 'on' : 'off'}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DomainCard({ domain }: { domain: FeatureFlagsCatalogDomain }) {
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-300">
            {domain.label}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Posture: {domain.posture.replace('_', ' ')}
            {domain.shipped ? ' · shipped final features' : ''}
            {domain.env_prefix ? ` · Env ${domain.env_prefix}*` : ''}
          </p>
          {domain.note ? <p className="text-xs text-gray-400 mt-1">{domain.note}</p> : null}
        </div>
        <Link
          to={domain.admin_href}
          className="text-sm text-orange-300/90 hover:underline shrink-0"
        >
          Open {domain.label}
        </Link>
      </div>
      <FlagTable flags={domain.flags || {}} />
    </section>
  );
}

export const AdminFeatureFlags: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<FeatureFlagsCatalog | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.adminFeatureFlagsCatalog();
      setCatalog(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load feature flags');
      setCatalog(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-8 p-6 max-w-4xl">
      <PageHeader
        eyebrow="Admin"
        title="Feature flags"
        description="Inventory of shipped programs and kill switches. Flag infrastructure remains for future experimental features."
      />

      {loading ? <p className="text-sm text-gray-500">Loading…</p> : null}
      {error ? (
        <AdminQueueState
          variant="error"
          title="Could not load feature flags"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}

      {!loading && catalog ? (
        <>
          <p className="text-sm text-gray-400">
            {catalog.note ||
              'Current program domains are shipped (always on). Emergency off uses env DOMAIN_FLAG_KEY=0. Kill switches remain SQL/ops only.'}
          </p>

          <div className="space-y-5">
            {(catalog.domains || []).map((domain) => (
              <DomainCard key={domain.id} domain={domain} />
            ))}
          </div>

          {catalog.kill_switches ? (
            <section className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-5 space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/90">
                Kill switches
              </h2>
              <p className="text-xs text-gray-400">{catalog.kill_switches.note}</p>
              <p className="text-xs text-gray-500">
                Posture: {catalog.kill_switches.posture.replace('_', ' ')} (missing row = on)
              </p>
              <FlagTable flags={catalog.kill_switches.flags || {}} />
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default AdminFeatureFlags;
